// src/api/notification.api.js
import api from './axios';

/**
 * Create a task assigned notification and send it via SignalR
 * @param {Object} data - Notification data
 * @param {string} data.userId - The user ID to notify
 * @param {string} data.productionLotId - The production lot ID
 * @param {string} data.productionOrderId - The production order ID
 * @param {string} data.productionTaskId - The production task ID
 * @returns {Promise<Object>} Created notification
 */
export const createTaskAssignedNotification = async (data) => {
  try {
    console.log('🔔 Creating task assigned notifications for users:', data.userIds);
    
    // Handle both single userId (for backward compatibility) and array
    const payload = {
      userIds: Array.isArray(data.userIds) ? data.userIds : 
               (data.userId ? [data.userId] : []),
      productionLotId: data.productionLotId,
      productionOrderId: data.productionOrderId,
      productionTaskId: data.productionTaskId
    };
    
    const response = await api.post('/api/notifications/task-assigned', payload);
    
    console.log('✅ Notifications created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating task assigned notifications:', error);
    throw error;
  }
};

/**
 * Get all notifications for the authenticated user
 * @returns {Promise<Object>} List of user notifications
 */
export const getUserNotifications = async () => {
  try {
    const response = await api.get('/api/notifications/user');
    return response.data;
  } catch (error) {
    console.error('Error fetching user notifications:', error);
    throw error;
  }
};

/**
 * Mark a specific notification as read
 * @param {string} notificationId - The ID of the notification to mark as read
 * @returns {Promise<Object>} Updated notification
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const response = await api.put(`/api/notifications/${encodeURIComponent(notificationId)}/read`);
    return response.data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Deactivate/delete a notification (soft delete)
 * @param {string} notificationId - The ID of the notification to deactivate
 * @returns {Promise<Object>} Deactivation result
 */
export const deactivateNotification = async (notificationId) => {
  try {
    const response = await api.delete(`/api/notifications/${encodeURIComponent(notificationId)}`);
    return response.data;
  } catch (error) {
    console.error('Error deactivating notification:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read for the authenticated user
 * @returns {Promise<Object>} Update result
 */
export const markAllNotificationsAsRead = async () => {
  try {
    const response = await api.put('/api/notifications/read-all');
    return response.data;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Clear all notifications for the authenticated user (deactivate all)
 * @returns {Promise<Object>} Clear result
 */
export const clearAllNotifications = async () => {
  try {
    const response = await api.delete('/api/notifications/clear-all');
    // Some APIs return 204 No Content, or different structure
    return response.data || { isSuccess: true };
  } catch (error) {
    console.error('Error clearing all notifications:', error);
    throw error;
  }
};

/**
 * Get unread notifications count for the authenticated user
 * @returns {Promise<number>} Count of unread notifications
 */
export const getUnreadNotificationsCount = async () => {
  try {
    const response = await api.get('/api/notifications/unread-count');

    if (response?.data?.isSuccess) {
      return response.data.data; // this is the int count
    }

    return 0;
  } catch (error) {
    console.error('Error getting unread notifications count:', error);
    return 0;
  }
};


export default {
  createTaskAssignedNotification,
  getUserNotifications,
  markNotificationAsRead,
  deactivateNotification,
  markAllNotificationsAsRead,
  clearAllNotifications,
  getUnreadNotificationsCount
};